import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PickersRangeCalendarHeaderProps } from '@mui/x-date-pickers-pro/PickersRangeCalendarHeader';
import {
  DateRange,
  DateRangePicker,
  DateRangeValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { startOfDay, endOfDay } from 'date-fns';

import { getLocale, translate } from '@/components/common/translateFunctions';
import { CustomCalendarHeader } from './CustomCalendarHeader';
import { localeText, shortcutsItems } from './DateRangeSelectBase';
import { isValidDate } from '@/utilities/dateUtils';

import './DateTimeSelect.css';

type DateRangeSelectProps = {
  setSelectedDates: (from: Date, to: Date) => void;
  currentValue: { from: Date | null; to: Date | null };
  disabled?: boolean;
  onSameRangeSelect?: () => void;
};

export function DateRangeSelect({
  setSelectedDates,
  currentValue,
  disabled = false,
  onSameRangeSelect,
}: DateRangeSelectProps) {
  const { t, i18n } = useTranslation();
  const acceptedValue = useRef<DateRange<Date> | undefined>(undefined);
  const hasReachedEndPosition = useRef(false);
  const hasCompletedRangeSelection = useRef(false);
  const hasAcceptedValue = useRef(false);
  const [displayValue, setDisplayValue] = useState<{ from: Date | null; to: Date | null }>(currentValue);
  const MINDATE = new Date('2010-01-01T00:00:00Z');
  const MAXDATE = new Date();

  function onAccept(newValue: DateRange<Date>, context: PickerChangeHandlerContext<DateRangeValidationError>) {
    hasAcceptedValue.current = true;
    const [selectedFrom, selectedTo] = newValue;

    if (!selectedFrom || !selectedTo) return;
    if (!isValidDate(selectedFrom) || !isValidDate(selectedTo)) return;

    let from: Date = selectedFrom;
    let to: Date = selectedTo;

    if (from < MINDATE) from = MINDATE;
    if (from > MAXDATE) from = MAXDATE;

    if (to < MINDATE) to = MINDATE;
    if (to > MAXDATE) to = MAXDATE;

    if (from > to) {
      [from, to] = [to, from];
    }

    const normalizedDate: [Date, Date] = [startOfDay(from), endOfDay(to)];

    if (context.source === 'view') {
      // ha egerrel valasztott akkor egybol elsul
      setSelectedDates(normalizedDate[0], normalizedDate[1]);
    } else {
      // egyebkent csak eltesszuk
      acceptedValue.current = normalizedDate;
    }
  }

  function submit() {
    if (!acceptedValue.current || !acceptedValue.current[0] || !acceptedValue.current[1]) return;
    setSelectedDates(acceptedValue.current[0], acceptedValue.current[1]);
    setDisplayValue({ from: acceptedValue.current[0], to: acceptedValue.current[1] });
    acceptedValue.current = undefined; // nem fogjuk 2x submit-olni
  }

  function onChange(newValue: DateRange<Date>) {
    setDisplayValue({ from: newValue[0], to: newValue[1] });
  }

  function handleOpen() {
    hasReachedEndPosition.current = false;
    hasCompletedRangeSelection.current = false;
    hasAcceptedValue.current = false;
  }

  function handleClose() {
    if (hasCompletedRangeSelection.current && !hasAcceptedValue.current) {
      onSameRangeSelect?.();
    }
  }

  function handleRangePositionChange(rangePosition: 'start' | 'end') {
    if (rangePosition === 'end') {
      hasReachedEndPosition.current = true;
      return;
    }

    if (hasReachedEndPosition.current) {
      hasCompletedRangeSelection.current = true;
    }
  }

  const locale = getLocale(i18n.language);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DateRangePicker
        closeOnSelect={true}
        className="mui-datetime-selector"
        value={[displayValue.from, displayValue.to]}
        onChange={onChange}
        onAccept={onAccept}
        onOpen={handleOpen}
        onClose={handleClose}
        onRangePositionChange={handleRangePositionChange}
        disabled={disabled}
        minDate={MINDATE}
        currentMonthCalendarPosition={2}
        disableFuture
        slots={{
          calendarHeader: CustomCalendarHeader,
        }}
        slotProps={{
          shortcuts: {
            id: 'shortcuts_panel',
            items: translate(shortcutsItems, t),
          },
          field: {
            id: 'date_picker_panel',
            onBlur: submit,
            onKeyDown: (event) => {
              if (event.code === 'Enter' || event.code === 'NumpadEnter') submit();
            },
          },
          textField: {
            placeholder: 'YYYY.MM.DD. - YYYY.MM.DD.',
            inputProps: { 'data-qa-element-id': 'single_date_input' },
          },
          popper: {
            id: 'date_picker_popup',
          },
          openPickerButton: {
            id: 'open_calendar_picker_button',
          },
          day: (ownerState) => {
            const d = ownerState.day;
            const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            return { id: `day_of_month_${formatted}` };
          },
          calendarHeader: {
            maxDate: new Date(),
          } as Partial<PickersRangeCalendarHeaderProps>,
        }}
        localeText={localeText(t)}
      />
    </LocalizationProvider>
  );
}
